import { getEndPoint } from '@udecode/plate';
import { useCallback, useState } from 'react';
import { Range } from 'slate';
import { BufferEditor } from '../../BufferEditor/types.plate';
import { CHARACTER_LIMIT_HIGHLIGHT_KEY } from './createCharacterLimitHighlightPlugin';
import { findCharacterLimitPoint } from '../../helpers/findCharacterLimitPoint';

export type CharacterLimitRange = Range & {
  [CHARACTER_LIMIT_HIGHLIGHT_KEY]: boolean;
};

const defaultCharacterCounter = (text: string) => text?.length;

type usePlateCharacterLimits = (
  /**
   * A Plate editor instance
   */
  editor: BufferEditor,

  /**
   * The total amount of allowed characters in the editor
   */
  characterLimit: number | null,

  /**
   * A callback to calculate the current character count of
   * a given text string.
   * Defaults to (text) => text.length
   */
  getTextCharacterCount: (text: string) => number,

  /**
   * An optional callback to calculate any additional characters
   * to be added, for example, if attachments change the character count
   */
  getAdditionalCharacterCount?: (text: string) => number
) => {
  /**
   * Decorate callback to be passed as part of editableProps
   * The callback is run internally by Slate on every change for every node
   */
  decorate: () => CharacterLimitRange[];

  /**
   * Callback to recompute character limit ranges
   */
  calculateCharacterLimitRanges: () => void;
};

export const usePlateCharacterLimits: usePlateCharacterLimits = (
  editor,
  characterLimit,
  getTextCharacterCount = defaultCharacterCounter,
  getAdditionalCharacterCount
) => {
  const [characterLimitRanges, setCharacterLimitRanges] = useState<
    CharacterLimitRange[]
  >([]);

  const calculateCharacterLimitRanges = useCallback(() => {
    const pointWhereLimitIsExceeded = findCharacterLimitPoint(
      editor,
      characterLimit,
      getTextCharacterCount,
      getAdditionalCharacterCount
    );

    if (!pointWhereLimitIsExceeded) {
      setCharacterLimitRanges([]);
    } else {
      setCharacterLimitRanges([
        {
          anchor: pointWhereLimitIsExceeded,
          focus: getEndPoint(editor, []),
          [CHARACTER_LIMIT_HIGHLIGHT_KEY]: true,
        },
      ]);
    }
  }, [
    editor,
    characterLimit,
    getTextCharacterCount,
    getAdditionalCharacterCount,
  ]);

  const decorate = useCallback(
    () => characterLimitRanges,
    [characterLimitRanges]
  );

  return {
    decorate,
    calculateCharacterLimitRanges,
  };
};
